import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Globe2, Mail, Lock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function AuthShell({ children, heading, sub }) {
  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-white flex-col justify-between p-12 relative overflow-hidden">
        <div className="relative z-10 flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-lg bg-sky-500 flex items-center justify-center"><Globe2 className="h-6 w-6" /></div>
          <div><p className="font-bold font-display">PharmaExport</p><p className="text-[11px] text-slate-400 tracking-wide">GROWTH OS</p></div>
        </div>
        <div className="relative z-10 max-w-md">
          <h2 className="text-3xl font-bold font-display leading-tight">International buyer interest → Export business → Measurable ROI</h2>
          <p className="mt-4 text-slate-300 text-sm leading-relaxed">The export growth operating system for pharmaceutical manufacturers and exporters. Convert enquiries into orders — and prove the return on every rupee invested.</p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[["12.6×", "Avg ROI"], ["₹18.4L", "Export revenue"], ["5", "Orders won"]].map(([v, l]) => (
              <div key={l}><p className="text-2xl font-bold font-display text-sky-400">{v}</p><p className="text-xs text-slate-400">{l}</p></div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">Trusted by export-oriented pharma companies worldwide</div>
        <div className="absolute -right-20 -bottom-20 h-80 w-80 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="absolute right-40 top-20 h-60 w-60 rounded-full bg-emerald-500/10 blur-3xl" />
      </div>
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="h-10 w-10 rounded-lg bg-sky-500 flex items-center justify-center"><Globe2 className="h-6 w-6 text-white" /></div>
            <p className="font-bold font-display text-lg">PharmaExport</p>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-display">{heading}</h1>
          <p className="mt-2 text-sm text-slate-500">{sub}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { setUser, setCompany } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      const me = await api.get("/auth/me");
      setCompany(me.data.company);
      toast.success("Welcome back!");
      navigate(data.user.role === "super_admin" ? "/platform" : "/dashboard");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell heading="Sign in to your account" sub="Access your export growth dashboard">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-sm">Work email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" className="pl-9 bg-white" />
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Password</Label>
            <Link to="/forgot-password" className="text-xs text-sky-600 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className="pl-9 bg-white" />
          </div>
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full bg-slate-900 hover:bg-slate-800 text-white gap-2">
          {loading ? "Signing in…" : "Sign in"} <ArrowRight className="h-4 w-4" />
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3"><div className="flex-1 h-px bg-slate-200" /><span className="text-xs text-slate-400">OR</span><div className="flex-1 h-px bg-slate-200" /></div>
      <Button variant="outline" onClick={googleLogin} data-testid="google-login-button" className="w-full gap-2 bg-white">
        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" /> Continue with Google
      </Button>
      <p className="mt-6 text-center text-sm text-slate-500">New to PharmaExport? <Link to="/signup" className="text-sky-600 font-medium hover:underline" data-testid="signup-link">Create an account</Link></p>
      <div className="mt-6 p-3 rounded-lg bg-slate-100 text-xs text-slate-500">
        <p className="font-medium text-slate-600 mb-1">Demo logins</p>
        <p>Company Admin: admin@globalcare.com / Demo@2026</p>
        <p>Sales Manager: manager@globalcare.com / Demo@2026</p>
      </div>
    </AuthShell>
  );
}

export function Signup() {
  const { setUser, setCompany } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ company_name: "", full_name: "", email: "", password: "", phone: "", country: "" });
  const [loading, setLoading] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", f);
      setUser(data.user);
      const me = await api.get("/auth/me");
      setCompany(me.data.company);
      toast.success("Account created! Let's set up your company.");
      navigate("/onboarding");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell heading="Create your company account" sub="Start turning buyer interest into export business">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label className="text-sm">Company name</Label><Input data-testid="signup-company-input" value={f.company_name} onChange={up("company_name")} required placeholder="ABC Pharma Pvt Ltd" className="bg-white" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-sm">Full name</Label><Input data-testid="signup-name-input" value={f.full_name} onChange={up("full_name")} required className="bg-white" /></div>
          <div className="space-y-1.5"><Label className="text-sm">Phone</Label><Input data-testid="signup-phone-input" value={f.phone} onChange={up("phone")} className="bg-white" /></div>
        </div>
        <div className="space-y-1.5"><Label className="text-sm">Work email</Label><Input data-testid="signup-email-input" type="email" value={f.email} onChange={up("email")} required className="bg-white" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-sm">Password</Label><Input data-testid="signup-password-input" type="password" value={f.password} onChange={up("password")} required minLength={6} className="bg-white" /></div>
          <div className="space-y-1.5"><Label className="text-sm">Country</Label><Input data-testid="signup-country-input" value={f.country} onChange={up("country")} placeholder="India" className="bg-white" /></div>
        </div>
        <Button type="submit" disabled={loading} data-testid="signup-submit-button" className="w-full bg-slate-900 hover:bg-slate-800 text-white gap-2">{loading ? "Creating…" : "Create account"} <ArrowRight className="h-4 w-4" /></Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">Already have an account? <Link to="/login" className="text-sky-600 font-medium hover:underline">Sign in</Link></p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <AuthShell heading="Reset your password" sub="We'll send you a secure reset link">
      {sent ? (
        <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700" data-testid="forgot-success">
          If an account exists for {email}, a reset link has been sent. Check your inbox.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label className="text-sm">Work email</Label><Input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-white" /></div>
          <Button type="submit" data-testid="forgot-submit-button" className="w-full bg-slate-900 hover:bg-slate-800 text-white">Send reset link</Button>
        </form>
      )}
      <p className="mt-6 text-center text-sm text-slate-500"><Link to="/login" className="text-sky-600 font-medium hover:underline">Back to sign in</Link></p>
    </AuthShell>
  );
}

export function ResetPassword() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [password, setPassword] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token, password }); toast.success("Password updated. Please sign in."); navigate("/login"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <AuthShell heading="Set a new password" sub="Choose a strong password for your account">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label className="text-sm">New password</Label><Input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="bg-white" /></div>
        <Button type="submit" data-testid="reset-submit-button" className="w-full bg-slate-900 hover:bg-slate-800 text-white">Update password</Button>
      </form>
    </AuthShell>
  );
}
